import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flame, Trophy } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';

interface ScoreSnapshot {
  score: number;
  streak: number;
  stageAdvances: number;
  dealsWon: number;
  tasksCompleted: number;
  activeDays: string[];
}

/**
 * Story 58.2 — daily score + streak gamification card.
 *
 * Shows today's score (visible "what did I do today" number), the
 * current consecutive-active-days streak, and a 14-day sparkline so
 * the rep can see whether their streak is at risk.
 */
export function RepDailyScoreCard() {
  const { t } = useTranslation();
  const [snap, setSnap] = useState<ScoreSnapshot | null>(null);

  useEffect(() => {
    apiClient
      .get<ScoreSnapshot>('/cockpit/rep/daily-score')
      .then(({ data }) => setSnap(data))
      .catch(() => setSnap(null));
  }, []);

  if (!snap) return null;

  // 14-day sparkline. Each day is a small column; filled if the user
  // had any score-positive activity that day. Today is right-aligned
  // so the rep reads "left to right = older".
  const sparkDays = 14;
  const todayIso = new Date().toISOString().slice(0, 10);
  const today = new Date(`${todayIso}T00:00:00Z`);
  const activeSet = new Set(snap.activeDays);
  const cells = Array.from({ length: sparkDays }, (_, i) => {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - (sparkDays - 1 - i));
    const iso = d.toISOString().slice(0, 10);
    return { iso, active: activeSet.has(iso), isToday: iso === todayIso };
  });

  return (
    <Card className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-sm">
          <Trophy className="h-6 w-6" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('cockpitRep.score_today')}
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-semibold tabular-nums">{snap.score}</span>
            <span className="text-xs text-muted-foreground">
              {snap.stageAdvances > 0 && (
                <span className="mr-2">
                  {snap.stageAdvances}× {t('cockpitRep.score_advances')}
                </span>
              )}
              {snap.dealsWon > 0 && (
                <span className="mr-2">
                  {snap.dealsWon}× {t('cockpitRep.score_won')}
                </span>
              )}
              {snap.tasksCompleted > 0 && (
                <span>
                  {snap.tasksCompleted}× {t('cockpitRep.score_tasks')}
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 rounded-full bg-amber-900/40 px-3 py-1 text-amber-200">
          <Flame className="h-4 w-4" />
          <span className="text-sm font-semibold tabular-nums">{snap.streak}</span>
          <span className="text-xs">
            {snap.streak === 1
              ? t('cockpitRep.streak_day')
              : t('cockpitRep.streak_days')}
          </span>
        </div>
        <div
          className="flex items-end gap-0.5"
          aria-label={t('cockpitRep.sparkline_aria') ?? '14-day activity'}
          title={t('cockpitRep.sparkline_aria') ?? '14-day activity'}
        >
          {cells.map((c) => (
            <div
              key={c.iso}
              className={`w-1.5 rounded-sm ${
                c.active
                  ? c.isToday
                    ? 'bg-primary'
                    : 'bg-emerald-500'
                  : 'bg-muted'
              }`}
              style={{ height: c.active ? 20 : 6 }}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}
